<template>
    <app-layout title="Settings">
        <form @submit.prevent="submit">
            <page-header>
                <template #title>Settings - Auth Codes</template>

                <template #actions>
                    <jet-button type="button" class="h-7 leading-none" @click="showAuthCodeModal = 'add'" >
                        Add Auth Code
                    </jet-button>
                </template>
            </page-header>

            <settings-navigation />

            <div class="flex px-8">
                <div class="flex gap-x-2 w-3/4">
                    <div class="flex flex-col flex-1">
                        <jet-input type="text" class="h-7" name="search" v-model="mainForm.search" @input="handleSubmit" placeholder="Search..."/>
                    </div>
                    <div class="self-end">
                        <jet-tertiary-button type="button" class="py-1 h-7" @click="handleReset" >
                            Reset
                        </jet-tertiary-button>
                    </div>
                </div>
            </div>

            <div class="px-8 mt-2">
                <div class="rounded-t overflow-hidden border border-b-0 w-3/4">
                    <table class="table w-full">
                        <thead>
                            <tr>
                                <sortable-header class="text-left" @sort="handleSort" :direction="sortDirection" :column="sortColumn" sort-by="name">Name</sortable-header>
                                <sortable-header class="text-left" @sort="handleSort" :direction="sortDirection" :column="sortColumn" sort-by="code">Code</sortable-header>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="auth_code in auth_codes.data" :key="auth_code.id" class="cursor-pointer" @click="editAuthCode(auth_code)">
                                <td class="text-left">
                                    {{ auth_code.name }}
                                </td>
                                <td class="text-left">
                                    {{ auth_code.code }}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div class="flex justify-between items-center mt-4 w-3/4">
                    <pagination class="mt-0 mb-0" :links="auth_codes.links" />

                    <div class="w-40">
                        <multiselect
                            v-model.number="mainForm.paginate"
                            :options="paginateOptions"
                            :canDeselect="false"
                            :canClear="false"
                            :classes="{ singleLabel: 'w-full px-2' }"
                            class="h-8 multi-sm multi-custom text-base"
                            openDirection="top"
                            @change="handleSubmit"
                        />
                    </div>
                </div>
            </div>

            <!-- Add / Edit authcode -->
            <jet-dialog-modal :show="showAuthCodeModal" @close="closeAuthCodeModal">
                <template #title>
                     <h3 class="text-2xl">{{ showAuthCodeModal == 'add' ? 'Add' : 'Edit' }} Auth Code</h3>
                </template>

                <template #content>
                    <form @submit.prevent="submitForm" class="my-6">
                        <div class="mb-2">
                            <jet-label value="Authorizer Name:" class="mb-0" style="min-width: 60px;" />
                            <jet-input v-model="form.name" type="text" class="w-full" />
                            <jet-input-error class="mt-1" :message="form.errors.name" />
                        </div>

                        <div>
                            <jet-label value="Code:" class="mb-0" style="min-width: 60px;" />
                            <jet-input v-model="form.code" type="text" class="w-full" />
                            <jet-input-error class="mt-1" :message="form.errors.code" />
                        </div>

                        <input type="submit" class="hidden" ref="submitFormButton" />
                    </form>
                </template>

                <template #footer>
                    <jet-danger-button v-if="showAuthCodeModal === 'edit'" class="mr-2" @click="showDeleteModal = true">
                        Delete
                    </jet-danger-button>

                    <jet-button class="mr-2" @click="$refs.submitFormButton.click()" :disabled="form.processing">
                        Save
                    </jet-button>

                    <jet-tertiary-button @click="closeAuthCodeModal">
                        Cancel
                    </jet-tertiary-button>
                </template>
            </jet-dialog-modal>

            <!-- Delete AuthCode -->
            <jet-confirmation-modal :show="showDeleteModal" @close="showDeleteModal = false">
                <template #title>
                    Delete Auth Code
                </template>

                <template #content>
                    Are you sure you would like to delete auth code?
                </template>

                <template #footer>
                    <jet-danger-button @click="handleDeleteAuthCode">
                        Yes
                    </jet-danger-button>

                    <jet-tertiary-button class="ml-2" @click="showDeleteModal = false">
                        No
                    </jet-tertiary-button>
                </template>
            </jet-confirmation-modal>
        </form>
    </app-layout>
</template>

<script>
import { useForm } from '@inertiajs/inertia-vue3';
import AppLayout from '@/Layouts/AppLayout.vue';
import PageHeader from '@/Layouts/partials/PageHeader.vue';
import JetLabel from '@/Jetstream/Label';
import JetInput from '@/Jetstream/Input';
import JetInputError from '@/Jetstream/InputError.vue';
import JetDialogModal from '@/Jetstream/DialogModal';
import JetConfirmationModal from '@/Jetstream/ConfirmationModal';
import JetButton from '@/Jetstream/Button';
import JetDangerButton from '@/Jetstream/DangerButton';
import JetTertiaryButton from '@/Jetstream/TertiaryButton';
import SettingsNavigation from '@/Pages/Settings/Partials/SettingsNavigation';
import Multiselect from '@vueform/multiselect';
import Pagination from '@/Jetstream/Pagination'
import SortableHeader from '@/Shared/SortableHeader';
import Toggle from '@vueform/toggle';

import '@vueform/multiselect/themes/default.css';
import '@vueform/toggle/themes/default.css';

export default {
    setup(props) {
        const mainForm = useForm({
            paginate: props.paginate,
            search: props.search,
            sortColumn: props.sortColumn,
            sortDirection: props.sortDirection,
        });

        const form = useForm({
            id: null,
            name: null,
            code: null,
        });

        return { mainForm, form }
    },

    components: {
        AppLayout,
        PageHeader,
        JetLabel,
        JetInput,
        JetInputError,
        JetDialogModal,
        JetConfirmationModal,
        JetButton,
        JetDangerButton,
        JetTertiaryButton,
        SettingsNavigation,
        Multiselect,
        Pagination,
        SortableHeader,
        Toggle,
    },

    props: [
        'auth_codes',
        'search',
        'sortColumn',
        'sortDirection',
        'paginate',
    ],

    data() {
        return {
            showAuthCodeModal: false,
            showDeleteModal: false,

            paginateOptions: [
                { value: 10, label: 'Show 10' },
                { value: 20, label: 'Show 20' },
                { value: 50, label: 'Show 50' },
                { value: 100, label: 'Show 100' },
            ],
        }
    },

    methods: {
        handleSubmit: _.debounce(function () {
            this.mainForm.get(route('settings.auth_codes.index'), { preserveState: true });
        }, 250),

        editAuthCode(data) {
            this.form.id = data.id;
            this.form.name = data.name;
            this.form.code = data.code;
            this.showAuthCodeModal = 'edit';
        },

        handleReset() {
            this.mainForm.search = '';
            this.mainForm.sortColumn = 'id';
            this.mainForm.sortDirection = 'asc';
            this.handleSubmit();
        },

        handleSort(sort) {
            this.mainForm.sortColumn = sort.column;
            this.mainForm.sortDirection = sort.direction;
            this.handleSubmit();
        },

        closeAuthCodeModal() {
            this.form.reset();
            this.form.clearErrors();
            this.showAuthCodeModal = false;
            this.showDeleteModal = false;
        },

        submitForm() {
             if (this.showAuthCodeModal === 'add') {
                this.form.post(this.route('settings.auth_codes.store'), {
                    onSuccess: () => this.closeAuthCodeModal()
                });

                return true;
            }

            this.form.put(this.route('settings.auth_codes.update', this.form.id), {
                onSuccess: () => this.closeAuthCodeModal()
            });
        },

        handleDeleteAuthCode() {
            this.form.delete(this.route('settings.auth_codes.destroy', this.form.id), {
                onSuccess: () => this.closeAuthCodeModal()
            });
        },
    },
}
</script>

<style scoped>
</style>
